import axios from "axios";
import ConstantsApi from "../constants/ConstantsApi";
import ApiService from "./ApiService";
import UtilsUnit from "../utils/UtilsUnit";

class NetworkInterceptor {
  constructor() {
    this.isRefreshing = false;
    this.failedQueue = [];

    this.api = axios.create({
      baseURL: ConstantsApi.BASE_URL,
    });

    this._initializeInterceptors();
  }

  _shouldSkipAccessToken(url) {
    const publicEndpoints = [ConstantsApi.GET_USER_DATA, ConstantsApi.REFRESH_TOKEN];
    return publicEndpoints.some((endpoint) => url.includes(endpoint));
  }

  _isRefreshEndpoint(url) {
    return url.includes(ConstantsApi.REFRESH_TOKEN);
  }

  _processQueue(error, token = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  _initializeInterceptors() {
    this.api.interceptors.request.use(
      (config) => {
        if (!this._shouldSkipAccessToken(config.url)) {
          const accessToken = UtilsUnit.getAccessToken();
          config.headers.Authorization = accessToken;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry && !this._isRefreshEndpoint(originalRequest.url)) {
          originalRequest._retry = true;

          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({
                resolve: (token) => {
                  originalRequest.headers.Authorization = token;
                  resolve(this.api(originalRequest));
                },
                reject: (err) => reject(err),
              });
            });
          }

          this.isRefreshing = true;

          try {
            const newAccessToken = await ApiService.refreshToken();
            this._processQueue(null, newAccessToken);
            originalRequest.headers.Authorization = newAccessToken;
            return this.api(originalRequest);
          } catch (err) {
            this._processQueue(err, null);
            return Promise.reject(err);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  get instance() {
    return this.api;
  }

  get(url, config = {}) {
    return this.api.get(url, config);
  }

  post(url, data = {}, config = {}) {
    return this.api.post(url, data, config);
  }

  put(url, data = {}, config = {}) {
    return this.api.put(url, data, config);
  }

  delete(url, config = {}) {
    return this.api.delete(url, config);
  }
}

const networkInterceptor = new NetworkInterceptor();
export default networkInterceptor;
